import {useAppSelector} from '@/hooks';
import {getCurrentPageDuration} from '@Components/poster-editor/poster-editor-reducer';
import type {ItemModifyingState} from '../timeline.types';
import {TIMELINE_RIGHT_MARGIN, TIMELINE_HORIZONTAL_PADDING} from '../timeline.types';
import {DEFAULT_PIXELS_PER_SECOND} from '../timeline.types';
import {timeToPixels} from '../utils/timeline-calculations';
import {useTimelineScrollContainerRef} from './useTimelineRefs';

export const useTimelineWidthPerSecond = (): number => {
  return useAppSelector((state) => state.timeline?.widthPerSecond ?? DEFAULT_PIXELS_PER_SECOND);
};

export const useTimelineHoverPosition = (): number | undefined => {
  return useAppSelector((state) => state.timeline?.timelineHoverPosition);
};

/**
 * Returns the current item modification state, or undefined if no item is being modified.
 */
export const useItemModifying = (): ItemModifyingState | undefined => {
  return useAppSelector((state) => state.timeline?.itemModifying);
};

/**
 * Returns true if any timeline item is currently being dragged.
 */
export const useIsTimelineItemDragging = (): boolean => {
  return useAppSelector((state) => state.timeline?.itemModifying?.action.type === 'dragging');
};

/**
 * Returns true if any timeline item is currently being resized.
 */
export const useIsTimelineItemResizing = (): boolean => {
  return useAppSelector((state) => state.timeline?.itemModifying?.action.type === 'resizing');
};

/**
 * Returns true if the specified item is currently being dragged.
 */
export const useIsItemDragging = (itemId: string): boolean => {
  return useAppSelector((state) => {
    const modifying = state.timeline?.itemModifying;
    return modifying?.itemId === itemId && modifying?.action.type === 'dragging';
  });
};

/**
 * Returns true if the specified item is currently being resized.
 */
export const useIsItemResizing = (itemId: string): boolean => {
  return useAppSelector((state) => {
    const modifying = state.timeline?.itemModifying;
    return modifying?.itemId === itemId && modifying?.action.type === 'resizing';
  });
};

/**
 * Returns true if the specified item is currently being modified (dragged or resized).
 */
export const useIsItemModifying = (itemId: string): boolean => {
  return useAppSelector((state) => state.timeline?.itemModifying?.itemId === itemId);
};

export const useTimelineWidth = (): number => {
  const pageDuration = useAppSelector(getCurrentPageDuration) ?? 0;
  const widthPerSecond = useTimelineWidthPerSecond();
  return timeToPixels(pageDuration, widthPerSecond) + TIMELINE_RIGHT_MARGIN;
};

/**
 * Returns a function that seeks the poster to the time corresponding to a clientX position.
 * Accounts for scroll offset, horizontal padding, and page start time.
 */
export const useTimelineSeekToClientX = (): ((clientX: number) => void) => {
  const scrollContainerRef = useTimelineScrollContainerRef();
  const widthPerSecond = useTimelineWidthPerSecond();
  const currentPageDuration = useAppSelector(getCurrentPageDuration);

  return (clientX: number): void => {
    if (!scrollContainerRef?.current || currentPageDuration === undefined) {
      return;
    }
    const containerRect = scrollContainerRef.current.getBoundingClientRect();
    const relativeX = clientX - containerRect.left + scrollContainerRef.current.scrollLeft - TIMELINE_HORIZONTAL_PADDING;
    const seekTime = Math.max(0, Math.min(relativeX / widthPerSecond, currentPageDuration - 0.01));
    void window.posterEditor?.whiteboard?.pause();
    void window.posterEditor?.whiteboard?.getCurrentPage().seek(seekTime);
  };
};
